import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { Calendar, Sparkles } from "lucide-react";

const initialBook = { name: "", phone: "", date: "", time: "19:30", guests: 2, notes: "" };
const initialQuote = {
  name: "", phone: "",
  event_type: "corporate", guests: 20, date: "", budget: "", preferences: "",
};

export default function Reservation({ initialTab = "book" }) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState(initialTab);
  const [book, setBook] = useState(initialBook);
  const [quote, setQuote] = useState(initialQuote);
  const [info, setInfo] = useState(null);

  useEffect(() => { setTab(initialTab); }, [initialTab]);
  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const onChangeBook = (e) => setBook((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onChangeQuote = (e) => setQuote((f) => ({ ...f, [e.target.name]: e.target.value }));

  const buildBookMessage = () => {
    const lng = i18n.language;
    const intro = lng === "en" ? "Hello, I'd like to book a table at Harry's Salotto e Cucina."
      : lng === "de" ? "Hallo, ich möchte einen Tisch bei Harry's Salotto e Cucina reservieren."
      : "Salve, vorrei prenotare un tavolo da Harry's Salotto e Cucina.";
    const L = lng === "en" ? { name: "Name", date: "Date", time: "Time", guests: "Guests", phone: "Phone", notes: "Notes" }
      : lng === "de" ? { name: "Name", date: "Datum", time: "Uhrzeit", guests: "Gäste", phone: "Telefon", notes: "Anmerkungen" }
      : { name: "Nome", date: "Data", time: "Orario", guests: "Ospiti", phone: "Telefono", notes: "Note" };
    const lines = [intro, "", `${L.name}: ${book.name}`, `${L.date}: ${book.date}`,
      `${L.time}: ${book.time}`, `${L.guests}: ${book.guests}`, `${L.phone}: ${book.phone}`];
    if (book.notes) lines.push(`${L.notes}: ${book.notes}`);
    return lines.join("\n");
  };

  const buildQuoteMessage = () => {
    const lng = i18n.language;
    const intro = lng === "en" ? "Hello, I'd like to request a quote for a private event at Harry's Salotto e Cucina."
      : lng === "de" ? "Hallo, ich möchte ein Angebot für eine private Veranstaltung bei Harry's Salotto e Cucina anfordern."
      : "Salve, vorrei richiedere un preventivo per un evento privato da Harry's Salotto e Cucina.";
    const L = lng === "en" ? { name: "Name", phone: "Phone", event: "Event type", guests: "Guests", date: "Date", budget: "Budget (€/p)", pref: "Preferences" }
      : lng === "de" ? { name: "Name", phone: "Telefon", event: "Event-Typ", guests: "Gäste", date: "Datum", budget: "Budget (€/P)", pref: "Wünsche" }
      : { name: "Nome", phone: "Telefono", event: "Tipo evento", guests: "Ospiti", date: "Data", budget: "Budget (€/p)", pref: "Preferenze" };
    const eventLabel = t(`quote.eventTypes.${quote.event_type}`);
    const lines = [intro, "", `${L.name}: ${quote.name}`, `${L.phone}: ${quote.phone}`,
      `${L.event}: ${eventLabel}`, `${L.guests}: ${quote.guests}`, `${L.date}: ${quote.date}`];
    if (quote.budget) lines.push(`${L.budget}: ${quote.budget}`);
    if (quote.preferences) lines.push(`${L.pref}: ${quote.preferences}`);
    return lines.join("\n");
  };

  const sendWhatsApp = (msg, successKey) => {
    if (!info?.whatsapp) { toast.error("WhatsApp non configurato"); return; }
    const phone = info.whatsapp.replace(/[^0-9]/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    toast.success(t(successKey));
  };

  return (
    <div data-testid="reservation-page" className="py-20 sm:py-28">
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C59] font-semibold">
          {t("reservation.eyebrow")}
        </p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-none mt-5">
          {t(tab === "book" ? "reservation.title" : "quote.title")}
        </h1>
        <p className="text-[#5C5C59] mt-6">
          {t(tab === "book" ? "reservation.subtitle" : "quote.subtitle")}
        </p>

        {/* Tab switcher */}
        <div className="mt-10 inline-flex border border-[#E5E3D8] bg-[#F3F1EC] p-1 rounded">
          <button
            onClick={() => setTab("book")}
            data-testid="tab-book"
            className={`px-5 py-2.5 text-sm tracking-wide inline-flex items-center gap-2 rounded transition-colors ${
              tab === "book" ? "bg-white text-[#8B3A3A] shadow-sm" : "text-[#5C5C59] hover:text-[#2C2C2A]"
            }`}
          >
            <Calendar className="w-4 h-4" strokeWidth={1.5} />
            {t("reservation.tabBook")}
          </button>
          <button
            onClick={() => setTab("quote")}
            data-testid="tab-quote"
            className={`px-5 py-2.5 text-sm tracking-wide inline-flex items-center gap-2 rounded transition-colors ${
              tab === "quote" ? "bg-white text-[#8B3A3A] shadow-sm" : "text-[#5C5C59] hover:text-[#2C2C2A]"
            }`}
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            {t("reservation.tabQuote")}
          </button>
        </div>

        <div className="mt-6 inline-flex items-center gap-2 ml-0 sm:ml-3 px-4 py-2 bg-[#25D366]/10 border border-[#25D366]/30 rounded text-sm text-[#1FAE54]">
          <WaIcon />
          {t("reservation.viaWhatsapp")}
        </div>

        {tab === "book" ? (
          <form onSubmit={(e) => { e.preventDefault(); sendWhatsApp(buildBookMessage(), "reservation.success"); }}
            className="mt-10 space-y-6" data-testid="reservation-form">
            <Field label={t("reservation.name")}>
              <input required name="name" value={book.name} onChange={onChangeBook} data-testid="res-name" className={inputCls} />
            </Field>
            <Field label={t("reservation.phone")}>
              <input required name="phone" value={book.phone} onChange={onChangeBook} data-testid="res-phone" className={inputCls} />
            </Field>
            <div className="grid sm:grid-cols-3 gap-6">
              <Field label={t("reservation.date")}>
                <input required type="date" name="date" value={book.date} onChange={onChangeBook} data-testid="res-date" className={inputCls} />
              </Field>
              <Field label={t("reservation.time")}>
                <input required type="time" name="time" value={book.time} onChange={onChangeBook} data-testid="res-time" className={inputCls} />
              </Field>
              <Field label={t("reservation.guests")}>
                <input required type="number" min="1" max="40" name="guests" value={book.guests} onChange={onChangeBook} data-testid="res-guests" className={inputCls} />
              </Field>
            </div>
            <Field label={t("reservation.notes")}>
              <textarea name="notes" rows="4" value={book.notes} onChange={onChangeBook} data-testid="res-notes" className={inputCls} />
            </Field>
            <SubmitBtn testid="res-submit" label={t("reservation.submit")} />
          </form>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); sendWhatsApp(buildQuoteMessage(), "quote.success"); }}
            className="mt-10 space-y-6" data-testid="quote-form">
            <Field label={t("quote.name")}>
              <input required name="name" value={quote.name} onChange={onChangeQuote} data-testid="quote-name" className={inputCls} />
            </Field>
            <Field label={t("quote.phone")}>
              <input required name="phone" value={quote.phone} onChange={onChangeQuote} data-testid="quote-phone" className={inputCls} />
            </Field>
            <div className="grid sm:grid-cols-3 gap-6">
              <Field label={t("quote.eventType")}>
                <select required name="event_type" value={quote.event_type} onChange={onChangeQuote} data-testid="quote-event-type" className={inputCls}>
                  {["birthday", "corporate", "anniversary", "wedding", "other"].map((k) => (
                    <option key={k} value={k}>{t(`quote.eventTypes.${k}`)}</option>
                  ))}
                </select>
              </Field>
              <Field label={t("quote.guests")}>
                <input required type="number" min="2" max="200" name="guests" value={quote.guests} onChange={onChangeQuote} data-testid="quote-guests" className={inputCls} />
              </Field>
              <Field label={t("quote.date")}>
                <input required type="date" name="date" value={quote.date} onChange={onChangeQuote} data-testid="quote-date" className={inputCls} />
              </Field>
            </div>
            <Field label={t("quote.budget")}>
              <input name="budget" value={quote.budget} onChange={onChangeQuote} data-testid="quote-budget" className={inputCls} placeholder="40-60" />
            </Field>
            <Field label={t("quote.preferences")}>
              <textarea name="preferences" rows="4" value={quote.preferences} onChange={onChangeQuote} data-testid="quote-preferences" className={inputCls} />
            </Field>
            <SubmitBtn testid="quote-submit" label={t("quote.submit")} />
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#E5E3D8] focus:border-[#8B3A3A] focus:ring-0 outline-none px-0 py-3 text-base placeholder:text-[#A6A6A0]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.2em] uppercase text-[#5C5C59] font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SubmitBtn({ testid, label }) {
  return (
    <button
      type="submit"
      data-testid={testid}
      className="w-full sm:w-auto px-10 py-4 bg-[#25D366] hover:bg-[#1FAE54] text-white transition-all hover:-translate-y-[1px] inline-flex items-center justify-center gap-2.5"
    >
      <WaIcon />
      {label}
    </button>
  );
}

function WaIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M20.52 3.48A11.88 11.88 0 0012.04 0C5.5 0 .2 5.3.2 11.84c0 2.09.55 4.13 1.6 5.93L0 24l6.36-1.66a11.83 11.83 0 005.68 1.45c6.54 0 11.84-5.3 11.84-11.83 0-3.16-1.23-6.13-3.36-8.48zm-8.47 18.22a9.84 9.84 0 01-5.02-1.38l-.36-.21-3.78.99 1.01-3.69-.24-.38a9.84 9.84 0 01-1.5-5.19c0-5.43 4.42-9.85 9.86-9.85 5.43 0 9.85 4.42 9.85 9.85-.01 5.44-4.43 9.85-9.85 9.85z"/></svg>
  );
}
